import type { Address } from "viem"
import type { Config } from "wagmi"
import { runInAction } from "mobx"
import { formatUnits } from "viem"
import { createActor, fromPromise, setup } from "xstate"

import type { IEventBus } from "@/domains/events"
import type { ILock } from "@/domains/lock"
import type { LockVenammQuoteService } from "@/domains/lock-venamm-quotes/lock-venamm-quote-service"
import type { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import type { ITokenLookup } from "@/domains/tokens/types"
import type { IVenammStore } from "@/domains/venamm"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { LcAddress } from "@/domains/core"
import { resetNftInteraction } from "@/domains/interactions"
import { ResetNftActionsAdapter } from "@/domains/interactions/reset-nft/reset-nft-actions-adapter"
import { approveVenammNft, sellVenammNft } from "@/domains/on-chain"

import type { VenammSellState } from "./venamm-sell-store"
import { VenammSellStore } from "./venamm-sell-store"

interface VenammSellOrchestratorContext {
  chainId: number
  store: VenammSellStore
  config: Config
  lock: ILock | null
  eventBus: IEventBus
  voterAddress: `0x${string}`
  veTokenAddress: `0x${string}`
  venAmmAddress: `0x${string}`
  walletStore: IWalletConnectionStore
  lockOnChainTransport: LockOnChainTransport
  tokenLookup: ITokenLookup
  venammStore: IVenammStore
  venammQuoteService: LockVenammQuoteService
}

export class VenammSellOrchestrator {
  public readonly store: VenammSellStore

  private actor: ReturnType<typeof createActor>
  private eventBus: IEventBus
  private currentNftId: number | null

  constructor(
    chainId: number,
    store: VenammSellStore,
    config: Config,
    lock: ILock | null,
    eventBus: IEventBus,
    voterAddress: `0x${string}`,
    veTokenAddress: `0x${string}`,
    venAmmAddress: `0x${string}`,
    walletStore: IWalletConnectionStore,
    lockOnChainTransport: LockOnChainTransport,
    tokenLookup: ITokenLookup,
    venammStore: IVenammStore,
    venammQuoteService: LockVenammQuoteService,
  ) {
    this.store = store
    this.eventBus = eventBus
    this.currentNftId = lock?.id ?? null

    const context: VenammSellOrchestratorContext & { __orchestrator?: VenammSellOrchestrator } = {
      chainId,
      store,
      config,
      lock,
      eventBus,
      voterAddress,
      veTokenAddress,
      venAmmAddress,
      walletStore,
      lockOnChainTransport,
      tokenLookup,
      venammStore,
      venammQuoteService,
      __orchestrator: this,
    }

    const machine = createVenammSellMachine(context)
    this.actor = createActor(machine)

    // Sync XState state with MobX store
    this.actor.subscribe((snapshot) => {
      runInAction(() => {
        // console.log("state changed to", snapshot.value)
        this.store.setState(snapshot.value as VenammSellState)
      })
    })

    // Start the actor
    this.actor.start()
  }

  destroy(): void {
    this.actor.stop()
  }

  startSellFlow(): void {
    this.actor.send({ type: "START_SELL" })
  }

  retryQuote(): void {
    this.store.clearQuoteError()
    this.actor.send({ type: "RETRY_QUOTE" })
  }

  retryReset(): void {
    this.store.clearResetError()
    this.actor.send({ type: "RETRY_RESET" })
  }

  retryApprove(): void {
    this.store.clearApproveError()
    this.actor.send({ type: "RETRY_APPROVE" })
  }

  retrySell(): void {
    this.store.clearSellError()
    this.actor.send({ type: "RETRY_SELL" })
  }

  executeReset(): void {
    this.actor.send({ type: "RESET" })
  }

  executeApprove(): void {
    this.actor.send({ type: "APPROVE" })
  }

  executeSell(): void {
    this.actor.send({ type: "SELL" })
  }

  reset(): void {
    this.store.reset()
    this.actor.send({ type: "CANCEL" })
  }

  cancel(): void {
    // Don't reset the quote - just clear errors so user can retry
    this.store.clearAllErrors()
    this.actor.send({ type: "CANCEL" })
  }

  updateLock(lock: ILock | null): void {
    const previousNftId = this.currentNftId
    this.currentNftId = lock?.id ?? null

    // Update the lock in the store first
    runInAction(() => {
      this.store.setLock(lock)
    })

    // If the NFT ID changed, reset to idle state
    if (previousNftId !== null && previousNftId !== this.currentNftId) {
      this.reset()
    }

    // Automatically fetch quote when lock is set
    if (lock) {
      const actorState = this.actor.getSnapshot().value
      if (actorState === "idle" && this.store.state === "idle") {
        // Start fetching quote automatically
        this.startSellFlow()
      }
    }
  }
}

// XState v5 Machine Definition
function createVenammSellMachine(context: VenammSellOrchestratorContext) {
  return setup({
    actors: {
      fetchQuote: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        // console.log("VenammSellOrchestrator: Fetching quote for lock", lock.id)

        // Use the LockVenammQuoteService to fetch the quote
        const quoteData = await context.venammQuoteService.fetchQuoteForLock(lock.id)

        if (!quoteData) {
          // console.error("VenammSellOrchestrator: Quote fetch returned null")
          throw new Error("Failed to fetch quote")
        }

        // console.log("VenammSellOrchestrator: Quote fetched successfully", quoteData)

        // Return the quote in the format expected by the store
        return {
          offer: quoteData.offer,
          value: quoteData.value,
          haircut: quoteData.haircut,
          rate: quoteData.rate,
          canSell: quoteData.canSell,
        }
      }),

      checkResetRequired: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        // Poll with exponential backoff: 200ms, 400ms, 800ms, 1600ms
        const maxAttempts = 4
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const delay = Math.min(200 * Math.pow(2, attempt), 1600)

          // Force refresh lock data from chain
          console.log("checkResetRequired - calling lock.forceRefresh", lock.id, `attempt ${attempt} of ${maxAttempts}`)
          await lock.forceRefresh()

          // Wait for refresh to complete
          await new Promise((resolve) => setTimeout(resolve, delay))

          // Check the updated state directly from the lock
          if (lock.isReset) {
            return { isReset: true }
          }

          // If this is not the last attempt, continue to next iteration
          if (attempt < maxAttempts - 1) {
            continue
          }
        }

        // After all attempts, return the final state
        return {
          isReset: lock.isReset,
        }
      }),

      executeReset: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        const onChainActions = new ResetNftActionsAdapter()

        const account = context.walletStore.getConnectedAddress()
        if (!account) {
          throw new Error("Account not connected")
        }

        const result = await resetNftInteraction({
          account,
          chainId: context.chainId,
          config: context.config,
          emitter: undefined,
          voterAddress: context.voterAddress,
          tokenId: lock.id,
          eventBus: context.eventBus,
          onChainActions,
        })

        if (!result || !result.success) {
          const errorMessage =
            typeof result === "object" && result !== null && "error" in result
              ? String(result.error)
              : "Reset transaction failed"
          throw new Error(errorMessage)
        }

        runInAction(() => {
          if (result.success && result.tx) {
            context.store.setResetTxHash(result.tx)
          }
        })

        return result
      }),

      checkApproval: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        // Poll with exponential backoff: 200ms, 400ms, 800ms, 1600ms
        const maxAttempts = 4
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          const delay = Math.min(200 * Math.pow(2, attempt), 1600)

          // Force refresh lock data from chain
          console.log("checkApproval - calling lock.forceRefresh", lock.id, `attempt ${attempt} of ${maxAttempts}`)
          await lock.forceRefresh()

          // Wait for refresh to complete
          await new Promise((resolve) => setTimeout(resolve, delay))

          const isApproved = await context.lockOnChainTransport.checkVenammApproval(
            context.venAmmAddress,
            context.veTokenAddress,
            lock.id,
          )

          console.log("isApproved", isApproved)

          if (isApproved) {
            return { isApproved: true }
          }

          // If this is not the last attempt, continue to next iteration
          if (attempt < maxAttempts - 1) {
            continue
          }
        }

        // After all attempts, return the final state
        const finalIsApproved = await context.lockOnChainTransport.checkVenammApproval(
          context.venAmmAddress,
          context.veTokenAddress,
          lock.id,
        )

        return { isApproved: finalIsApproved }
      }),

      executeApprove: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        const account = context.walletStore.getConnectedAddress()
        if (!account) {
          throw new Error("Account not connected")
        }

        const result = await approveVenammNft({
          chainId: context.chainId,
          config: context.config,
          veTokenAddress: context.veTokenAddress,
          venAmmAddress: context.venAmmAddress,
          account,
          tokenId: lock.id,
        })

        if (!result || !result.success) {
          const errorMessage =
            typeof result === "object" && result !== null && "error" in result
              ? String(result.error)
              : "Approval transaction failed"
          throw new Error(errorMessage)
        }

        runInAction(() => {
          if (result.success && result.tx) {
            context.store.setApproveTxHash(result.tx)
          }
        })

        return result
      }),

      executeSell: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        const account = context.walletStore.getConnectedAddress()
        if (!account) {
          throw new Error("Account not connected")
        }

        const result = await sellVenammNft({
          chainId: context.chainId,
          config: context.config,
          venAmmAddress: context.venAmmAddress,
          account,
          tokenId: lock.id,
        })

        if (!result || !result.success) {
          const errorMessage =
            typeof result === "object" && result !== null && "error" in result
              ? String(result.error)
              : "Sell transaction failed"
          throw new Error(errorMessage)
        }

        runInAction(() => {
          if (result.success && result.tx) {
            context.store.setSellTxHash(result.tx)
          }
        })

        return result
      }),
    },
    guards: {
      hasQuote: () => {
        return context.store.quote !== null
      },
    },
  }).createMachine({
    id: "venamm-sell",
    initial: "idle",
    on: {
      CANCEL: [
        {
          target: ".quote-ready",
          guard: "hasQuote",
        },
        {
          target: ".idle",
        },
      ],
    },
    states: {
      idle: {
        on: {
          START_SELL: {
            target: "quoting",
            guard: () => {
              return context.store.lock !== null
            },
          },
        },
      },
      quoting: {
        invoke: {
          src: "fetchQuote",
          onDone: {
            target: "quote-ready",
            actions: ({ event }: any) => {
              // console.log("VenammSellOrchestrator: onDone called with event", event)
              runInAction(() => {
                if (event?.output) {
                  // console.log("VenammSellOrchestrator: Setting quote in store", event.output)
                  context.store.setQuote(event.output)
                  // console.log("VenammSellOrchestrator: Quote set, store quote is now", context.store.quote)
                } else {
                  // If output is missing, set error and transition will still happen
                  // but we'll handle it in the UI
                  // console.error("VenammSellOrchestrator: Quote fetch succeeded but output is missing", event)
                  context.store.setQuoteError("Quote data is missing")
                }
              })
            },
          },
          onError: {
            target: "quote-failed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Failed to fetch quote"
                context.store.setQuoteError(errorMessage)
              })
            },
          },
        },
        on: {
          RETRY_QUOTE: {
            target: "quoting",
          },
        },
      },
      "quote-ready": {
        on: {
          START_SELL: {
            target: "checking-reset-required",
          },
          CANCEL: "idle",
        },
      },
      "quote-failed": {
        on: {
          RETRY_QUOTE: {
            target: "quoting",
          },
          CANCEL: "idle",
        },
      },
      "checking-reset-required": {
        invoke: {
          src: "checkResetRequired",
          onDone: [
            {
              target: "reset-required",
              guard: ({ event }) => {
                console.log("resetRequired - event", event)
                return !(event.output?.isReset ?? false)
              },
            },
            {
              target: "checking-approval",
              guard: ({ event }) => {
                console.log("resetNotRequired - event", event)
                return event.output?.isReset ?? false
              },
            },
          ],
          onError: {
            target: "quote-failed",
          },
        },
      },
      "reset-required": {
        on: {
          RESET: {
            target: "resetting",
          },
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      resetting: {
        invoke: {
          src: "executeReset",
          onDone: {
            target: "checking-reset-required",
          },
          onError: {
            target: "reset-failed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Reset transaction failed"
                context.store.setResetError(errorMessage)
              })
            },
          },
        },
        on: {
          RETRY_RESET: {
            target: "resetting",
          },
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      "reset-failed": {
        on: {
          RETRY_RESET: {
            target: "resetting",
          },
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      "checking-approval": {
        invoke: {
          src: "checkApproval",
          onDone: [
            {
              target: "approving",
              guard: ({ event }) => {
                console.log("isNotApproved - event", event)
                return !(event.output?.isApproved ?? false)
              },
            },
            {
              target: "selling",
              guard: ({ event }) => {
                console.log("isApproved - event", event)
                return event.output?.isApproved ?? false
              },
            },
          ],
          onError: {
            target: "quote-failed",
          },
        },
      },
      approving: {
        invoke: {
          src: "executeApprove",
          onDone: {
            target: "waiting-for-approval-confirmation",
          },
          onError: {
            target: "approve-failed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Approval transaction failed"
                context.store.setApproveError(errorMessage)
              })
            },
          },
        },
        on: {
          RETRY_APPROVE: {
            target: "approving",
          },
        },
      },
      "waiting-for-approval-confirmation": {
        // Wait 2 seconds to ensure contract state is updated after transaction confirmation
        // This helps avoid timing issues where the transaction is confirmed but state hasn't propagated
        after: {
          2000: {
            target: "checking-approval",
          },
        },
        on: {
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      "approve-failed": {
        on: {
          RETRY_APPROVE: {
            target: "approving",
          },
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      selling: {
        invoke: {
          src: "executeSell",
          onDone: {
            target: "complete",
          },
          onError: {
            target: "sell-failed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Sell transaction failed"
                context.store.setSellError(errorMessage)
              })
            },
          },
        },
        on: {
          RETRY_SELL: {
            target: "selling",
          },
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      "sell-failed": {
        on: {
          RETRY_SELL: {
            target: "selling",
          },
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
      complete: {
        type: "final",
        on: {
          CANCEL: [
            {
              target: "quote-ready",
              guard: "hasQuote",
            },
            {
              target: "idle",
            },
          ],
        },
      },
    },
  })
}
